import React from 'react';
import {useRef} from 'react';

import {motion} from 'framer-motion';
import {useInView} from 'framer-motion';
import {useIntl} from 'react-intl';

export const CallToAction: React.FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useIntl();
  const ref = useRef(null);
  const isInView = useInView(ref, {once: true, amount: 0.3});

  const ctaButtons = [
    {
      title: intl.formatMessage({id: 'home.callToAction.preorderTitle'}),
      description: intl.formatMessage({
        id: 'home.callToAction.preorderDescription',
      }),
      buttonText: intl.formatMessage({id: 'home.callToAction.preorderButton'}),
      primary: true,
      href: '/store',
    },
    // {
    //   title: intl.formatMessage({id: 'home.callToAction.betaTitle'}),
    //   description: intl.formatMessage({
    //     id: 'home.callToAction.betaDescription',
    //   }),
    //   buttonText: intl.formatMessage({id: 'home.callToAction.betaButton'}),
    //   primary: false,
    //   href: 'https://stryd.typeform.com/to/Q9Okuupv',
    // },
    {
      title: intl.formatMessage({id: 'home.callToAction.learnTitle'}),
      description: intl.formatMessage({
        id: 'home.callToAction.learnDescription',
      }),
      buttonText: intl.formatMessage({id: 'home.callToAction.learnButton'}),
      primary: false,
      href: 'https://blog.stryd.com',
    },
  ];

  const comparisonFeatures = [
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature1'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_1'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_1'}),
      highlight: true,
    },
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature2'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_2'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_2'}),
      highlight: true,
    },
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature3'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_3'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_3'}),
      highlight: true,
    },
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature4'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_4'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_4'}),
      highlight: true,
    },
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature5'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_5'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_5'}),
      highlight: true,
    },
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature6'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_6'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_6'}),
      highlight: true,
    },
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature7'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_7'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_7'}),
      highlight: true,
    },
    {
      feature: intl.formatMessage({id: 'home.callToAction.comparisonFeature8'}),
      gen4: intl.formatMessage({id: 'home.callToAction.comparisonGen4_8'}),
      gen5: intl.formatMessage({id: 'home.callToAction.comparisonGen5_8'}),
      highlight: true,
    },
  ];

  return (
    <section
      ref={ref}
      className="py-16 bg-gradient-to-br from-gray-900 via-orange-900 to-black text-white"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          // initial={{opacity: 0, y: 50}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.8, ease: 'easeOut'}}
        >
          <h2 className="text-4xl md:text-6xl font-light mb-6">
            {intl.formatMessage({id: 'home.callToAction.mainTitle'})}
          </h2>
          <p className="text-xl md:text-2xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
            {intl.formatMessage({id: 'home.callToAction.mainDescription'})}
          </p>
        </motion.div>

        {/* CTA Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {ctaButtons.map((cta, index) => (
            <motion.div
              key={index}
              className={`relative rounded-3xl p-8 overflow-hidden transition-all duration-300 hover:scale-105 h-full ${
                cta.primary
                  ? 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-2xl'
                  : 'bg-gray-800 hover:bg-gray-700'
              }`}
              // initial={{opacity: 0, y: 30}}
              // animate={isInView ? {opacity: 1, y: 0} : {}}
              // transition={{duration: 0.8, delay: 0.4 + index * 0.2}}
            >
              {/* Background decoration */}
              {cta.primary && (
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
              )}

              <div className="relative z-10 flex flex-col h-full">
                <motion.h3
                  className="text-2xl font-semibold mb-4"
                  // initial={{opacity: 0}}
                  // animate={isInView ? {opacity: 1} : {}}
                  // transition={{duration: 0.6, delay: 0.8 + index * 0.2}}
                >
                  {cta.title}
                </motion.h3>

                <motion.p
                  className={`flex-grow leading-relaxed mb-8 ${
                    cta.primary ? 'text-white/90' : 'text-gray-400'
                  }`}
                  // initial={{opacity: 0}}
                  // animate={isInView ? {opacity: 1} : {}}
                  // transition={{duration: 0.6, delay: 1.0 + index * 0.2}}
                >
                  {cta.description}
                </motion.p>

                <motion.a
                  href={cta.href}
                  className={`text-center inline-block px-8 py-4 rounded-2xl font-semibold transition-all duration-300 mt-auto ${
                    cta.primary
                      ? 'bg-white text-black hover:bg-gray-100 hover:shadow-lg'
                      : 'bg-blue-600 text-white hover:bg-blue-500'
                  }`}
                  // initial={{opacity: 0, scale: 0.8}}
                  // animate={isInView ? {opacity: 1, scale: 1} : {}}
                  // transition={{duration: 0.6, delay: 1.2 + index * 0.2}}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                >
                  {cta.buttonText}
                </motion.a>

                {/* Special badge for primary CTA */}
                {/* {cta.primary && (
                  <motion.div
                    className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium"
                    initial={{opacity: 0, scale: 0}}
                    animate={isInView ? {opacity: 1, scale: 1} : {}}
                    transition={{duration: 0.5, delay: 1.5}}
                  >
                    Limited Time
                  </motion.div>
                )} */}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Generation Comparison */}
        <motion.div
          className="bg-gray-800 rounded-3xl p-8"
          // initial={{opacity: 0, y: 30}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.8, delay: 1.5}}
        >
          <motion.h3
            className="text-3xl font-semibold text-center mb-8"
            // initial={{opacity: 0}}
            // animate={isInView ? {opacity: 1} : {}}
            // transition={{duration: 0.6, delay: 1.8}}
          >
            {intl.formatMessage({id: 'home.callToAction.comparisonTitle'})}
          </motion.h3>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-600">
                  <th className="text-left py-4 px-4 font-semibold">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-blue-400">
                    Updates
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((item, index) => (
                  <motion.tr
                    key={index}
                    className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors"
                    // initial={{opacity: 0, x: -20}}
                    // animate={isInView ? {opacity: 1, x: 0} : {}}
                    // transition={{duration: 0.5, delay: 2.0 + index * 0.1}}
                  >
                    <td className="py-4 px-4 font-medium">{item.feature}</td>
                    <td className="py-4 px-4 text-center">
                      <span
                        className={`${
                          item.highlight
                            ? 'text-blue-400 font-semibold'
                            : 'text-white'
                        }`}
                      >
                        {item.gen5}
                      </span>
                      {item.highlight && (
                        <motion.span
                          className="ml-2 text-xs bg-blue-600 px-2 py-1 rounded-full"
                          // initial={{opacity: 0, scale: 0}}
                          // animate={isInView ? {opacity: 1, scale: 1} : {}}
                          // transition={{duration: 0.3, delay: 2.5 + index * 0.1}}
                        >
                          New
                        </motion.span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          className="text-center mt-16"
          // initial={{opacity: 0, y: 30}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.8, delay: 2.5}}
        >
          <motion.p
            className="text-xl text-white-400 mb-8"
            // initial={{opacity: 0}}
            // animate={isInView ? {opacity: 1} : {}}
            // transition={{duration: 0.6, delay: 2.8}}
          >
            {intl.formatMessage({id: 'home.callToAction.communityTitle'})}
          </motion.p>

          <motion.div
            className="flex justify-center gap-4"
            // initial={{opacity: 0, scale: 0.8}}
            // animate={isInView ? {opacity: 1, scale: 1} : {}}
            // transition={{duration: 0.6, delay: 3.0}}
          >
            <div className="bg-gray-700 rounded-2xl px-6 py-3 flex items-center gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium">
                {intl.formatMessage({id: 'home.callToAction.benefit1'})}
              </span>
            </div>

            <div className="bg-gray-700 rounded-2xl px-6 py-3 flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium">
                {intl.formatMessage({id: 'home.callToAction.benefit2'})}
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
